import { DocumentTextIcon, ChartBarIcon } from "@heroicons/react/outline";
import { useMemo, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { responseIsFunctionOrTool } from "../utils/logUtils";

import { GroupInfoBlock } from "./GroupInfoBlock";
import LoadingSpinner from "./LoadingSpinner";
import { SidebarRequestLogCard } from "./SidebarRequestLogCard";
import BreadcrumbsPageWrapper from "./UnifiedRegistry/BreadcrumbsPageWrapper";

import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useGroupRequests } from "@/queries";
import { hydrateSidebarSignalBubbles } from "@/routes/request-logs/search/signal-search-utils";
import { useWorkspaceSignalNames } from "@/routes/request-logs/search/useWorkspaceSignalNames";
import {
  buildSharedGroupFilters,
  buildWorkspaceAnalyticsPath,
} from "@/routes/request-logs/search/buildReviewAnalyticsPath";
import { createStructuredSearchDefaultDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { SESSION_STORAGE } from "@/constants";
import { RequestPreview } from "@/types/requests";
import { stripReviewLogsDrawerIntent } from "@/utils/requestLogUrlParams";

export const GroupContent = ({ groupId }: { groupId: string }) => {
  const authContext = useAuth();
  const { workspaceId } = useParams();
  const { activeWorkspaceId } = useUser();
  const location = useLocation();
  const requests = useGroupRequests(groupId, authContext?.userToken || "");
  const signalNamesById = useWorkspaceSignalNames(authContext?.userToken, activeWorkspaceId);

  const data = requests?.data ?? [];

  const noRequests = data.cardinality === 0;
  const noRequestsFound = noRequests && !requests.isLoading;
  const ref = useRef<HTMLDivElement | null>(null);

  const reviewLogsHref =
    workspaceId !== undefined
      ? `/workspace/${workspaceId}/request-logs${stripReviewLogsDrawerIntent(location.search)}`
      : undefined;

  const groupAnalyticsHref = useMemo(() => {
    if (workspaceId === undefined || requests.isLoading) {
      return undefined;
    }
    const payload = requests.data;
    if (!payload || payload.cardinality === 0 || !payload.requests?.length) {
      return undefined;
    }
    const filters = buildSharedGroupFilters(payload.requests);
    return buildWorkspaceAnalyticsPath({
      workspaceId,
      baseSearch: location.search,
      filters,
      dateRange: createStructuredSearchDefaultDateRange(),
      openAnalyticsTab: true,
    });
  }, [workspaceId, requests.isLoading, requests.data, location.search]);

  return (
    <BreadcrumbsPageWrapper
      items={[
        {
          id: 0,
          label: "Request Logs",
          href: reviewLogsHref,
          icon: <DocumentTextIcon className="h-4 w-4" />,
        },
        {
          id: 1,
          label: "Group",
          icon: <DocumentTextIcon className="h-4 w-4" />,
        },
      ]}
      showBackButton
    >
      {groupAnalyticsHref ? (
        <div className="mb-4 flex justify-end px-20">
          <Link
            to={groupAnalyticsHref}
            onClick={() =>
              sessionStorage.setItem(
                SESSION_STORAGE.REQUESTS_ANALYTICS_RETURN_URL,
                `${location.pathname}${location.search}${location.hash}`,
              )
            }
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            <ChartBarIcon className="h-4 w-4" />
            View analytics for this group
          </Link>
        </div>
      ) : null}
      <div className="items-center justify-center space-y-2 px-20 pb-10 pt-10">
        <GroupInfoBlock groupId={groupId} />
        {!requests.isLoading &&
          data.requests.map((item: RequestPreview, idx: number) => {
            const signals = hydrateSidebarSignalBubbles(item.metadata, signalNamesById);

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div className="mx-auto max-w-xs" key={idx}>
                <SidebarRequestLogCard
                  active={false}
                  logId={item.id}
                  descriptor={item.engine || item.function_name}
                  startTime={item.request_start_time}
                  tags={item.tags}
                  signals={signals}
                  starred={item.is_starred}
                  prompt={item.prompt_preview || "<preview temporarily unavailable>"}
                  responseIsFunction={responseIsFunctionOrTool(item)}
                  response={item.request_response_preview || "<preview temporarily unavailable>"}
                  type={item.type}
                  cardinality={item.cardinality}
                  promptName={item.prompt_name}
                  provider={item.provider_type}
                  providerId={item.provider_id}
                  status={item.status}
                  errorType={item.error_type}
                  errorMessage={item.error_message}
                />
              </div>
            );
          })}
        {(groupId === "" || requests.isLoading) && (
          <div ref={ref} className="flex justify-center py-2">
            <LoadingSpinner size={5} />
          </div>
        )}
        {noRequestsFound && (
          <div className="flex justify-center py-2">
            <p className="text-gray-500">This group is empty.</p>
          </div>
        )}
      </div>
    </BreadcrumbsPageWrapper>
  );
};
